import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { X, Cookie, Settings } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  })

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent')
    if (!consent) {
      // Delay to show banner after page load
      setTimeout(() => setIsVisible(true), 1000)
    }
  }, [])

  const saveConsent = (
    accepted: boolean,
    customPreferences?: typeof preferences,
  ) => {
    const consentData = {
      timestamp: new Date().toISOString(),
      accepted,
      preferences: customPreferences || {
        necessary: true,
        analytics: accepted,
        marketing: accepted,
      },
    }

    localStorage.setItem('cookieConsent', JSON.stringify(consentData))
    setIsVisible(false)
    setShowSettings(false)
  }

  const handleAcceptAll = () => {
    saveConsent(true, { necessary: true, analytics: true, marketing: true })
  }

  const handleRejectOptional = () => {
    saveConsent(false, { necessary: true, analytics: false, marketing: false })
  }

  const handleSavePreferences = () => {
    saveConsent(true, preferences)
  }

  if (!isVisible) return null

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 bg-background/95 backdrop-blur-sm border-t border-border shadow-lg animate-in slide-in-from-bottom duration-300">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Cookie className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-2">
                  Este site utiliza cookies
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Usamos cookies para melhorar sua experiência, personalizar
                  conteúdo e analisar nosso tráfego. Ao clicar em "Aceitar
                  todos", você concorda com o uso de todos os cookies. Você pode
                  gerenciar suas preferências clicando em "Configurar".{' '}
                  <a
                    href="/privacidade"
                    className="text-primary hover:underline"
                    target="_blank"
                  >
                    Saiba mais
                  </a>
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettings(true)}
                className="gap-2 flex-1 md:flex-none"
              >
                <Settings className="h-4 w-4" />
                Configurar
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRejectOptional}
                className="flex-1 md:flex-none"
              >
                Recusar opcionais
              </Button>
              <Button
                size="sm"
                onClick={handleAcceptAll}
                className="flex-1 md:flex-none"
              >
                Aceitar todos
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Configurações de Cookies</DialogTitle>
            <DialogDescription>
              Gerencie suas preferências de cookies. Os cookies necessários não
              podem ser desativados.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-4 p-4 bg-muted/50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Label className="text-base font-semibold">
                      Cookies Necessários
                    </Label>
                    <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded">
                      Sempre ativo
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Essenciais para o funcionamento do site. Incluem cookies de
                    sessão, autenticação e preferências básicas. Não podem ser
                    desativados.
                  </p>
                </div>
                <Switch checked={true} disabled />
              </div>

              <div className="flex items-start justify-between gap-4 p-4 border rounded-lg">
                <div className="flex-1">
                  <Label className="text-base font-semibold mb-2 block">
                    Cookies Analíticos
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Coletam informações sobre como você usa nosso site, como
                    páginas visitadas e tempo de navegação. Nos ajudam a
                    melhorar a experiência do usuário.
                  </p>
                </div>
                <Switch
                  checked={preferences.analytics}
                  onCheckedChange={checked =>
                    setPreferences({ ...preferences, analytics: checked })
                  }
                />
              </div>

              <div className="flex items-start justify-between gap-4 p-4 border rounded-lg">
                <div className="flex-1">
                  <Label className="text-base font-semibold mb-2 block">
                    Cookies de Marketing
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Utilizados para exibir anúncios relevantes e medir a
                    eficácia de campanhas publicitárias. Podem rastrear sua
                    navegação em diferentes sites.
                  </p>
                </div>
                <Switch
                  checked={preferences.marketing}
                  onCheckedChange={checked =>
                    setPreferences({ ...preferences, marketing: checked })
                  }
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowSettings(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button onClick={handleSavePreferences} className="flex-1">
                Salvar Preferências
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default CookieConsent
